import {useTheme} from 'styled-components';

import {useRef, useState, useEffect, RefObject, useCallback} from 'react';

interface LayoutMeasurements {
  topElementPosition: {top: number; left: number} | null;
  bottomElementHeight: number | null;
  heightToViewportBottom: number | null;
  availableHeight: number | null;
}

interface UseLayoutMeasurementsReturn {
  topElementRef: RefObject<HTMLDivElement | null>;
  bottomElementRef: RefObject<HTMLDivElement | null>;
  measurements: LayoutMeasurements;
  recalculate: () => void;
}

const BASE_FONT_SIZE = 16;

/**
 * Custom hook to measure layout dimensions for any two elements where the top element
 * should fill remaining space after accounting for the bottom element.
 * Automatically recalculates measurements on window resize and scroll events.
 *
 * @param dependencies - Array of dependencies that should trigger recalculation
 * @returns Object containing refs, measurements, and recalculate function
 *
 * @example
 * // For Timeline + MessageForm
 * const {topElementRef: timelineRef, bottomElementRef: messageFormRef, measurements} = useLayoutMeasurements([conversationId]);
 *
 * @example
 * // For Comments + Input
 * const {topElementRef: commentsRef, bottomElementRef: inputRef, measurements} = useLayoutMeasurements([resourceId]);
 */
export function useLayoutMeasurements(dependencies: unknown[] = []): UseLayoutMeasurementsReturn {
  const theme = useTheme();
  const topElementRef = useRef<HTMLDivElement>(null);
  const bottomElementRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [measurements, setMeasurements] = useState<LayoutMeasurements>({
    topElementPosition: null,
    bottomElementHeight: null,
    heightToViewportBottom: null,
    availableHeight: null,
  });

  const calculateMeasurements = useCallback(() => {
    if (!topElementRef.current || !bottomElementRef.current) {
      return false;
    }

    const topElementRect = topElementRef.current.getBoundingClientRect();
    const bottomElementRect = bottomElementRef.current.getBoundingClientRect();

    if (topElementRect.height === 0 || bottomElementRect.height === 0) {
      return false;
    }

    const viewportHeight = window.innerHeight;

    const topElementPosition = {
      top: topElementRect.top,
      left: topElementRect.left,
    };

    const bottomElementHeight = bottomElementRect.height;
    const heightToViewportBottom = viewportHeight - topElementRect.top;
    const oneThemeStep = theme.space.step * BASE_FONT_SIZE;
    const onePaddingStep = oneThemeStep * oneThemeStep;

    const availableHeight = Math.max(0, heightToViewportBottom - onePaddingStep);

    setMeasurements({
      topElementPosition,
      bottomElementHeight,
      heightToViewportBottom,
      availableHeight,
    });

    return true;
  }, [theme.space.step]);

  const calculateMeasurementsWithRetry = useCallback(() => {
    const success = calculateMeasurements();

    if (!success) {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      retryTimeoutRef.current = setTimeout(() => {
        calculateMeasurementsWithRetry();
      }, 50);
    }
  }, [calculateMeasurements]);

  const throttledCalculateMeasurements = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      calculateMeasurements();
    });
  }, [calculateMeasurements]);

  useEffect(() => {
    calculateMeasurementsWithRetry();

    let resizeObserver: ResizeObserver | null = null;

    const setupResizeObserver = () => {
      if (topElementRef.current && bottomElementRef.current && !resizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          calculateMeasurements();
        });

        resizeObserver.observe(topElementRef.current);
        resizeObserver.observe(bottomElementRef.current);
        resizeObserver.observe(document.body);
      }
    };

    setupResizeObserver();

    const setupTimer = setTimeout(setupResizeObserver, 100);

    const handleResize = () => {
      calculateMeasurements();
    };

    const handleScroll = () => {
      throttledCalculateMeasurements();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, {passive: true});
    document.addEventListener('scroll', handleScroll, {passive: true});

    const setupScrollListeners = () => {
      const scrollListeners: Array<{element: Element; handler: () => void}> = [];
      let currentElement = topElementRef.current?.parentElement;

      while (currentElement) {
        const computedStyle = window.getComputedStyle(currentElement);
        const isScrollable =
          computedStyle.overflow === 'auto' ||
          computedStyle.overflow === 'scroll' ||
          computedStyle.overflowY === 'auto' ||
          computedStyle.overflowY === 'scroll';

        if (isScrollable) {
          const handler = () => {
            throttledCalculateMeasurements();
          };
          currentElement.addEventListener('scroll', handler, {passive: true});
          scrollListeners.push({element: currentElement, handler});
        }

        currentElement = currentElement.parentElement;
      }

      return scrollListeners;
    };

    const scrollListeners = setupScrollListeners();

    return () => {
      clearTimeout(setupTimer);

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);

      scrollListeners.forEach(({element, handler}) => {
        element.removeEventListener('scroll', handler);
      });

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [calculateMeasurementsWithRetry, throttledCalculateMeasurements]);

  useEffect(() => {
    calculateMeasurementsWithRetry();
  }, dependencies);

  return {
    topElementRef,
    bottomElementRef,
    measurements,
    recalculate: calculateMeasurements,
  };
}
